/* eslint-disable function-paren-newline */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable prettier/prettier */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import useShortAccessFilters from '../../../hooks/useShortAccessFilters';
import useAvailableCategories from '../../../hooks/useAvailableCategories';
import useAvailableSchedulesOptions from '../../../hooks/useAvailableSchedulesOptions';
import useAvailableSchedulesProvidersOptions from '../../../hooks/useAvailableSchedulesProvidersOptions';
import useAvailableStops from '../../../hooks/useAvailableStops';
import useAvailableTripOptions from '../../../hooks/useAvailableTripOptions';
import useAvailableReclineOptions from '../../../hooks/useAvailableReclineOptions';
import { saveShortAccessFilters } from '../../../utils/shortAccessFilters';
import ShortAccessFiltersList from './ShortAccessFiltersList';
import { filtersTypes } from '../../../constants/filtersTypes';
import useFiltersContext from '../../../hooks/useFilters';
import { findFilterOptionByValue, isFilterValueEqual } from '../../../utils/tripFilters';

const { CATEGORIES, DEPARTURE_TIME, STOPS, TRIP_OPTIONS, RECLINE_OPTIONS } = filtersTypes;

/**
 * ShortAccessFilters component.
 * Renders a list of short access filter options for trip search.
 * @param {Object} props - onSelect, way, isProviderList, isOpenTicketList.
 * @param {Function} props.onSelect - Callback function when a filter option is selected.
 * @param {string} props.way - The direction/way of the trip (e.g., 'outbound', 'return').
 * @param {boolean} props.isProviderList - Whether the current view is a provider list.
 * @param {boolean} props.isOpenTicketList - Whether the current view shows open tickets.
 * @returns {React.Element|null} The rendered filter list component or null if no filters available.
 */
export const ShortAccessFilters = ({ onSelect, way, isProviderList, isOpenTicketList }) => {
  const scheduleOptions = useAvailableSchedulesOptions(way, true);
  const scheduleProviderOptions = useAvailableSchedulesProvidersOptions(way, true);
  const availableCategories = useAvailableCategories(way);
  const availableStops = useAvailableStops(way, true);
  const availableTripOptions = useAvailableTripOptions(way, true);
  const availableReclineOptions = useAvailableReclineOptions(way);

  const {
    scheduleFilters,
    stopsFilters,
    categoriesFilters,
    scheduleFiltersProviders,
    tripOptionsFilters,
    reclineOptionsFilters,
  } = useFiltersContext();

  const [shortAccessFilters, setShortAccessFilters] = useShortAccessFilters();
  const persistedShortAccessFilters = useRef(shortAccessFilters);
  const visibleShortAccessFilters = useRef(shortAccessFilters);

  /**
   * Validates if a filter option is available based on its type and current context.
   * @param {Object} filter - The filter object to validate.
   * @param {string} filter.filterType - The type of filter (CATEGORIES, DEPARTURE_TIME, etc.).
   * @param {*} filter.value - The value of the filter option.
   * @returns {boolean} True if the filter is available, false otherwise.
   */
  const validateAvailability = ({ filterType, value }) => {
    if (filterType === CATEGORIES) {
      if (isProviderList || isOpenTicketList) return false;
      return availableCategories?.find((category) => category.value === value)?.hasAvailability;
    }
    if (filterType === DEPARTURE_TIME) {
      const scheduleOptionsToUse = isProviderList ? scheduleProviderOptions : scheduleOptions;
      return scheduleOptionsToUse?.find((schedule) => schedule.value === value)?.isAvailable;
    }
    if (filterType === STOPS && !isProviderList) {
      return availableStops?.some((filter) => filter.value === value);
    }
    if (filterType === TRIP_OPTIONS) {
      const foundOption = findFilterOptionByValue(availableTripOptions, value);
      return foundOption?.hasAvailability;
    }
    if (filterType === RECLINE_OPTIONS) {
      const foundOption = findFilterOptionByValue(availableReclineOptions, value);
      return foundOption?.hasAvailability;
    }
  };

  /**
   * Checks if a trip filter option is currently active.
   * @param {*} value - The filter option value to check (can be primitive or object).
   * @returns {boolean|Object} True if active, false if not, or the matching option object.
   */
  const isActiveTripFilterOption = (value) => {
    if (typeof value === 'object') {
      return tripOptionsFilters?.find((option) => JSON.stringify(option) === JSON.stringify(value));
    }
    return tripOptionsFilters.includes(value);
  };

  /**
   * Checks if a recline filter option is currently active.
   * @param {*} value - The filter option value to check (can be primitive or object).
   * @returns {boolean|Object} True if active, false if not, or the matching option object.
   */
  const isActiveReclineFilterOption = (value) => {
    if (!reclineOptionsFilters?.length) {
      return false;
    }
    if (typeof value === 'object') {
      return reclineOptionsFilters.find((option) => isFilterValueEqual(option, value));
    }
    return reclineOptionsFilters.includes(value);
  };

  /**
   * Function to check if a filter option is active base on the filters state.
   */
  useEffect(() => {
    if (!isEqual(shortAccessFilters, visibleShortAccessFilters.current)) {
      persistedShortAccessFilters.current = shortAccessFilters;
    }

    // The different filters options are checked extracting the one actives
    const activeScheduleFilters = !isProviderList
      ? (scheduleOptions || []).filter((scheduleOption) =>
          (scheduleFilters || []).includes(scheduleOption.value),
        )
      : [];

    const activeCategoriesFilters = (availableCategories || []).filter((availableCategory) =>
      (categoriesFilters || []).includes(availableCategory.value),
    );

    const activeProviderScheduleFilters = isProviderList
      ? scheduleProviderOptions?.filter((scheduleProviderOption) =>
          scheduleFiltersProviders?.includes(scheduleProviderOption.value),
        )
      : [];

    const activeStopsFilters =
      availableStops?.filter((availableStop) => stopsFilters?.includes(availableStop.value)) || [];

    const activeTripOptionsFilters =
      availableTripOptions?.filter((availableTripOption) =>
        isActiveTripFilterOption(availableTripOption.value),
      ) || [];

    const activeReclineOptionsFilters =
      availableReclineOptions?.filter((availableReclineOption) =>
        isActiveReclineFilterOption(availableReclineOption.value),
      ) || [];

    // For recline options, we want to always show them if they have availability
    // not just when they are active
    const alwaysVisibleReclineOptions = availableReclineOptions || [];

    const activeFilters = [
      ...activeScheduleFilters,
      ...activeProviderScheduleFilters,
      ...activeCategoriesFilters,
      ...activeStopsFilters,
      ...activeTripOptionsFilters,
      ...activeReclineOptionsFilters,
    ];

    /**
     * Helper function to compare filter values (handles both primitive and object values)
     * @param {*} value1 - First value to compare
     * @param {*} value2 - Second value to compare
     * @returns {boolean} True if values are equal, false otherwise
     */
    const compareFilterValues = (value1, value2) => isFilterValueEqual(value1, value2);

    const persistedFilters = persistedShortAccessFilters.current;

    const missedActiveFilters = activeFilters.filter(
      (activeFilter) =>
        !persistedFilters.some((filter) => compareFilterValues(filter.value, activeFilter.value)),
    );

    // For recline options, also check if we need to add them to always visible filters
    const missedReclineOptions = alwaysVisibleReclineOptions.filter(
      (reclineOption) =>
        !persistedFilters.some((filter) =>
          compareFilterValues(filter.value, reclineOption.value),
        ),
    );

    const newShortAccessFilters = [
      ...persistedFilters,
      ...missedActiveFilters,
      ...missedReclineOptions,
    ].map((filter) => ({
      ...filter,
      isActive: activeFilters.some((activeFilter) =>
        compareFilterValues(activeFilter.value, filter.value),
      ),
    }));

    /**
     * The filters that are not already available are removed from the array of short access filters that will be shown
     */
    const availableShortAccessFilters = newShortAccessFilters.filter((filter) =>
      validateAvailability(filter),
    );

    /**
     * The short access filters filtered by availability are set in the state. Because these are the filters
     * that are going to be shown.
     * The short access filters that are not filtered by availability are saved in the local storage.
     */
    if (!isEqual(availableShortAccessFilters, shortAccessFilters)) {
      visibleShortAccessFilters.current = availableShortAccessFilters;
      setShortAccessFilters(availableShortAccessFilters);
    } else {
      visibleShortAccessFilters.current = shortAccessFilters;
    }
    if (!isEqual(newShortAccessFilters, persistedFilters)) {
      persistedShortAccessFilters.current = newShortAccessFilters;
      saveShortAccessFilters(newShortAccessFilters);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scheduleFilters,
    stopsFilters,
    categoriesFilters,
    scheduleFiltersProviders,
    tripOptionsFilters,
    reclineOptionsFilters,
    availableCategories,
  ]);

  return shortAccessFilters.length ? (
    <ShortAccessFiltersList
      options={shortAccessFilters}
      onSelect={onSelect}
      isOpenTicketList={isOpenTicketList}
    />
  ) : null;
};

ShortAccessFilters.propTypes = {
  onSelect: PropTypes.func.isRequired,
  way: PropTypes.string.isRequired,
  isProviderList: PropTypes.bool,
  isOpenTicketList: PropTypes.bool,
};

export default ShortAccessFiltersList;
